import { Injectable } from '@angular/core';
import { z, ZodError, ZodSchema } from 'zod';

/**
 * Resultado de una validación
 */
export interface ValidationResult<T> {
  success: boolean;
  data?: T;
  errors?: z.ZodError;
  errorMessages?: string[];
}

/**
 * Servicio centralizado para validación de datos usando Zod
 * 
 * Este servicio proporciona métodos utilitarios para validar datos
 * contra esquemas Zod, con manejo de errores consistente y mensajes
 * de error amigables.
 */
@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  
  /**
   * Valida datos contra un esquema Zod
   * 
   * @param schema Esquema Zod a usar para validación
   * @param data Datos a validar
   * @returns Resultado de la validación con datos tipados o errores
   * 
   * @example
   * ```typescript
   * const result = this.validationService.validate(
   *   FichaSchema,
   *   datosDelServidor
   * );
   * 
   * if (result.success) {
   *   // result.data está tipado como FichaValidated
   *   console.log(result.data);
   * } else {
   *   // result.errorMessages contiene mensajes amigables
   *   console.error(result.errorMessages);
   * }
   * ```
   */
  validate<T>(schema: ZodSchema<T>, data: unknown): ValidationResult<T> {
    try {
      const validatedData = schema.parse(data);
      return {
        success: true,
        data: validatedData
      };
    } catch (error) {
      if (error instanceof ZodError) {
        return {
          success: false,
          errors: error,
          errorMessages: this.formatZodErrors(error)
        };
      }
      
      // Error inesperado
      return {
        success: false,
        errorMessages: [`Error de validación: ${error instanceof Error ? error.message : 'Error desconocido'}`]
      };
    }
  }

  /**
   * Valida datos de forma segura (no lanza excepciones)
   * Similar a validate pero siempre retorna un resultado
   * 
   * @param schema Esquema Zod
   * @param data Datos a validar
   * @returns Resultado de validación
   */
  safeValidate<T>(schema: ZodSchema<T>, data: unknown): ValidationResult<T> {
    const result = schema.safeParse(data);
    
    if (result.success) {
      return {
        success: true,
        data: result.data
      };
    }
    
    return {
      success: false,
      errors: result.error,
      errorMessages: this.formatZodErrors(result.error)
    };
  }

  /**
   * Valida y transforma datos, aplicando transformaciones del esquema
   * 
   * @param schema Esquema Zod con transformaciones
   * @param data Datos a validar y transformar
   * @returns Datos validados y transformados
   * @throws ZodError si la validación falla
   */
  validateAndTransform<T>(schema: ZodSchema<T>, data: unknown): T {
    return schema.parse(data);
  }

  /**
   * Valida un array de datos
   * 
   * @param schema Esquema Zod para cada elemento
   * @param data Array de datos a validar
   * @returns Array de resultados de validación
   */
  validateArray<T>(
    schema: ZodSchema<T>, 
    data: unknown[]
  ): Array<ValidationResult<T>> {
    return data.map((item, index) => {
      const result = this.validate(schema, item);
      if (!result.success && result.errorMessages) {
        // Agregar índice al mensaje de error
        result.errorMessages = result.errorMessages.map(
          msg => `[Índice ${index}]: ${msg}`
        );
      }
      return result;
    });
  }

  /**
   * Formatea errores de Zod en mensajes legibles
   * 
   * @param error Error de Zod
   * @returns Array de mensajes de error formateados
   */
  private formatZodErrors(error: ZodError): string[] {
    return error.issues.map((err) => {
      const path = err.path.length > 0 
        ? `Campo "${err.path.join('.')}"` 
        : 'Datos';
      
      let message = `${path}: ${err.message}`;
      
      // Agregar información adicional según el tipo de error
      if (err.code === 'invalid_type') {
        const invalidTypeErr = err as any; // ZodIssue con propiedades expected/received
        if (invalidTypeErr.expected && invalidTypeErr.received) {
          message += ` (esperado: ${invalidTypeErr.expected}, recibido: ${invalidTypeErr.received})`;
        }
      }
      
      return message;
    });
  }

  /**
   * Convierte un esquema Zod a JSON Schema
   * Útil para documentación, OpenAPI, o validación en otros sistemas
   * 
   * @param schema Esquema Zod
   * @param options Opciones para la conversión (target, metadata, etc.)
   * @returns JSON Schema equivalente
   * 
   * @example
   * ```typescript
   * const jsonSchema = this.validationService.toJSONSchema(
   *   FichaSchema,
   *   { target: 'openapi-3.0' }
   * );
   * // Usar jsonSchema en documentación OpenAPI
   * ```
   */
  toJSONSchema(schema: ZodSchema, options?: {
    target?: 'draft-2020-12' | 'draft-07' | 'draft-04' | 'openapi-3.0';
    metadata?: any;
  }): any {
    // Nota: z.toJSONSchema() está disponible en Zod 4+
    // Si usas una versión anterior, necesitarás una librería como zod-to-json-schema
    if (typeof (z as any).toJSONSchema === 'function') {
      return (z as any).toJSONSchema(schema, options);
    }
    
    // Fallback: retornar un esquema básico
    console.warn('z.toJSONSchema() no está disponible. Considera actualizar a Zod 4+ o usar zod-to-json-schema');
    return { type: 'object' };
  }

  /**
   * Crea un esquema Zod desde un JSON Schema
   * Útil para generar validación desde especificaciones OpenAPI
   * 
   * @param jsonSchema JSON Schema a convertir
   * @returns Esquema Zod equivalente
   * 
   * @example
   * ```typescript
   * const jsonSchema = { /* desde OpenAPI *\/ };
   * const zodSchema = this.validationService.fromJSONSchema(jsonSchema);
   * ```
   */
  fromJSONSchema(jsonSchema: any): ZodSchema {
    // Nota: z.fromJSONSchema() está disponible en Zod 4+
    if (typeof (z as any).fromJSONSchema === 'function') {
      return (z as any).fromJSONSchema(jsonSchema);
    }
    
    throw new Error('z.fromJSONSchema() no está disponible. Considera actualizar a Zod 4+');
  }
}
